/**
 * MeetingTranscriptTab — 「逐字稿」页签。
 *
 * 有时间戳分段时：音频回放 + 检索 + 分段列表（说话人/时间戳/正文，支持点击
 * 回放、行内改字、切换高亮、说话人批量改名）。手工粘贴的纪要没有分段，回退
 * 到纯文本渲染——这一路径与页签改名前的既有行为一致，不是新增能力。
 */

import { useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Input, Modal, Space, Typography, message } from "antd";
import { FileTextOutlined, SearchOutlined } from "@ant-design/icons";
import {
  useImportMeetingTranscript,
  useMeetingAudioUrl,
  useMeetingTranscript,
  useRenameTranscriptSpeaker,
  useUpdateTranscriptSegments,
} from "../../api/queries";
import type { Meeting } from "../../types/meeting";
import { useDebouncedValue } from "../../hooks";
import { matchTranscriptSegments } from "../../utils/meetingTranscriptSegments";
import EmptyState from "../common/EmptyState";
import MeetingSpeakerRenameModal from "./MeetingSpeakerRenameModal";
import MeetingTranscriptSegmentRow from "./MeetingTranscriptSegmentRow";

const { Text } = Typography;
const { TextArea } = Input;

interface MeetingTranscriptTabProps {
  meeting: Meeting;
  active: boolean;
}

export default function MeetingTranscriptTab({ meeting, active }: MeetingTranscriptTabProps) {
  const [messageApi, contextHolder] = message.useMessage();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 200);
  const [renameSpeaker, setRenameSpeaker] = useState<string | null>(null);
  const [manualImportOpen, setManualImportOpen] = useState(false);
  // 哪一段正在保存——不能把 updateSegmentsMut.isPending 整体传给每一行：那样
  // 编辑第 0 段时，第 1、2…段的保存/高亮按钮也会一起转圈，误导用户以为自己
  // 没点中的行也在处理。
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [transcriptForm] = Form.useForm<{ text: string; lang?: string }>();
  const audioRef = useRef<HTMLAudioElement>(null);

  const hasAudio = !!meeting.audioStorageKey;
  const transcriptQuery = useMeetingTranscript(meeting.id, active);
  const audioQuery = useMeetingAudioUrl(meeting.id, active && hasAudio);
  const importTranscriptMut = useImportMeetingTranscript();
  const updateSegmentsMut = useUpdateTranscriptSegments();
  const renameSpeakerMut = useRenameTranscriptSpeaker();

  const segments = transcriptQuery.data?.segments ?? null;
  const plainText = transcriptQuery.data?.text ?? meeting.transcriptText;

  const matches = useMemo(
    () => matchTranscriptSegments(segments ?? [], debouncedSearch),
    [segments, debouncedSearch],
  );
  const searching = debouncedSearch.trim() !== "";

  const handleSeek = (startMs: number) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = startMs / 1000;
    void el.play().catch(() => {
      // 自动播放可能被浏览器策略拦截；用户可手动点播放器控件，不做二次提示打扰。
    });
  };

  const handleSaveText = (index: number, text: string) => {
    setSavingIndex(index);
    updateSegmentsMut.mutate(
      { id: meeting.id, data: { segments: [{ index, text }] } },
      {
        onError: (err) =>
          messageApi.error(err instanceof Error ? err.message : "保存失败"),
        onSettled: () => setSavingIndex(null),
      },
    );
  };

  const handleToggleHighlight = (index: number) => {
    const current = segments?.find((s) => s.index === index);
    if (!current) return;
    setSavingIndex(index);
    updateSegmentsMut.mutate(
      { id: meeting.id, data: { segments: [{ index, highlight: !current.highlight }] } },
      {
        onError: (err) =>
          messageApi.error(err instanceof Error ? err.message : "更新失败"),
        onSettled: () => setSavingIndex(null),
      },
    );
  };

  const handleRenameSubmit = (to: string) => {
    if (!renameSpeaker) return;
    renameSpeakerMut.mutate(
      { id: meeting.id, data: { from: renameSpeaker, to } },
      {
        onSuccess: () => {
          messageApi.success(`已把「${renameSpeaker}」改为「${to}」`);
          setRenameSpeaker(null);
        },
        onError: (err) =>
          messageApi.error(err instanceof Error ? err.message : "改名失败"),
      },
    );
  };

  const handleManualImport = async () => {
    let values: { text: string; lang?: string };
    try {
      values = await transcriptForm.validateFields();
    } catch {
      return;
    }
    try {
      await importTranscriptMut.mutateAsync({
        id: meeting.id,
        data: { text: values.text, lang: values.lang || "zh" },
      });
      messageApi.success("纪要正文已导入");
      transcriptForm.resetFields();
      setManualImportOpen(false);
    } catch {
      messageApi.error("导入失败");
    }
  };

  return (
    <div data-testid="meeting-transcript-tab">
      {contextHolder}
      <Space style={{ marginBottom: "var(--jerry-sp-3)" }} wrap>
        <Button icon={<FileTextOutlined />} onClick={() => setManualImportOpen(true)}>
          手动导入纪要正文
        </Button>
        {segments && segments.length > 0 && (
          <Input
            allowClear
            placeholder="搜索逐字稿"
            prefix={<SearchOutlined />}
            style={{ width: 240 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="meeting-transcript-search"
          />
        )}
      </Space>

      {hasAudio && (
        <div style={{ marginBottom: "var(--jerry-sp-3)" }}>
          {audioQuery.data?.url ? (
            <audio
              ref={audioRef}
              controls
              preload="metadata"
              src={audioQuery.data.url}
              style={{ width: "100%" }}
              data-testid="meeting-transcript-audio"
            >
              您的浏览器不支持音频播放。
            </audio>
          ) : audioQuery.isLoading ? (
            <Text type="secondary">正在准备音频…</Text>
          ) : null}
        </div>
      )}

      {segments && segments.length > 0 ? (
        <div data-testid="meeting-transcript-segments" style={{ maxHeight: 480, overflow: "auto" }}>
          {searching && matches.length === 0 && (
            <Text type="secondary" data-testid="meeting-transcript-search-empty">
              没有匹配「{debouncedSearch}」的分段
            </Text>
          )}
          {matches.map(({ segment, ranges }) => (
            <MeetingTranscriptSegmentRow
              key={segment.index}
              segment={segment}
              matchRanges={ranges}
              canPlay={hasAudio}
              saving={savingIndex === segment.index}
              onSeek={handleSeek}
              onSaveText={handleSaveText}
              onToggleHighlight={handleToggleHighlight}
              onRenameSpeaker={setRenameSpeaker}
            />
          ))}
        </div>
      ) : plainText ? (
        <Typography.Paragraph
          style={{
            whiteSpace: "pre-wrap",
            background: "var(--jerry-gray-100)",
            padding: "var(--jerry-sp-3)",
            borderRadius: 6,
            maxHeight: 480,
            overflow: "auto",
          }}
        >
          {plainText}
        </Typography.Paragraph>
      ) : (
        <EmptyState title="暂无纪要正文" description="平台纪要可能还在生成，稍后重试；也可以直接粘贴纪要正文。" />
      )}

      {transcriptQuery.data?.status === "failed" && transcriptQuery.data.errorMessage && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: "var(--jerry-sp-3)" }}
          message="转写未完成"
          description={transcriptQuery.data.errorMessage}
        />
      )}

      <Modal
        title="导入纪要正文"
        open={manualImportOpen}
        onCancel={() => setManualImportOpen(false)}
        onOk={handleManualImport}
        confirmLoading={importTranscriptMut.isPending}
        okText="导入"
        destroyOnHidden
        width={600}
      >
        <Form form={transcriptForm} layout="vertical">
          <Form.Item name="text" label="纪要正文" rules={[{ required: true, message: "请粘贴纪要正文" }]}>
            <TextArea autoSize={{ minRows: 8, maxRows: 18 }} placeholder="粘贴会议纪要或转录正文…" />
          </Form.Item>
          <Form.Item name="lang" label="语言" initialValue="zh">
            <Input maxLength={10} />
          </Form.Item>
        </Form>
      </Modal>

      <MeetingSpeakerRenameModal
        open={!!renameSpeaker}
        from={renameSpeaker ?? ""}
        submitting={renameSpeakerMut.isPending}
        onCancel={() => setRenameSpeaker(null)}
        onSubmit={handleRenameSubmit}
      />
    </div>
  );
}
